import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { theme } from '_constants/theme';
import Icon from '_components/atomic/Icon';

const { colors, devices, fontSizes, spaces } = theme;

const StyledContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: ${spaces.large4X}px;

  @media ${devices.tablet} {
    gap: ${spaces.small - 2}px;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledCard = styled(forwardRef(({ maxCol, color, ...props }, ref) => (<div ref={ref} {...props} />)))`
  display: flex;
  flex-direction: column;

  padding: ${spaces.largeX + 2}px;

  border-radius: 20px;

  ${({ maxCol }) =>
    maxCol &&
    css`
      width: ${`calc(100% / ${maxCol} - ${
        (spaces.large4X * (maxCol - 1)) / maxCol
      }px)`};
    `}

  ${({ color }) =>
    color &&
    css`
      background-color: ${color};
    `}

  @media ${devices.tablet} {
    width: 100%;
    padding: ${spaces.regular}px;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledIconContainer = styled(forwardRef(({ desktop, mobile, ...props }, ref) => (<div ref={ref} {...props} />)))`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  margin-bottom: ${spaces.medium}px;

  ${({ mobile }) =>
    mobile &&
    css`
      display: none;
    `}

  @media ${devices.tablet} {
    justify-content: center;

    margin-bottom: ${spaces.regular}px;

    ${({ desktop }) =>
      desktop &&
      css`
        display: none;
      `}

    ${({ mobile }) =>
      mobile &&
      css`
        display: flex;
      `}
  }
`;

const StyledTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  margin-bottom: ${spaces.small - 2}px;

  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.large2X}px;
  line-height: 150%;
  color: ${colors.gray[800]};

  @media ${devices.tablet} {
    justify-content: center;
    font-size: ${fontSizes.medium}px;
  }
`;

const StyledDescription = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.medium}px;
  line-height: 150%;
  color: ${colors.gray[700]};

  @media ${devices.tablet} {
    justify-content: center;
    font-size: ${fontSizes.regular}px;
    text-align: center;
  }
`;

const FancyCards = ({ maxCol = 2, items = [] }) => {
  return (
    <StyledContainer>
      {items.map(
        ({ title, description, iconAlias, iconPalette, color }, idx) => (
          <StyledCard key={`fancy-card-${idx}`} maxCol={maxCol} color={color}>
            <StyledIconContainer desktop>
              <Icon
                alias={iconAlias}
                palette={iconPalette}
                height={80}
                width={80}
              />
            </StyledIconContainer>
            <StyledIconContainer mobile>
              <Icon
                alias={iconAlias}
                palette={iconPalette}
                height={60}
                width={60}
              />
            </StyledIconContainer>
            <StyledTitle>{title}</StyledTitle>
            <StyledDescription>{description}</StyledDescription>
          </StyledCard>
        )
      )}
    </StyledContainer>
  );
};

FancyCards.propTypes = {
  maxCol: PropTypes.number,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.node,
      description: PropTypes.node,
      iconAlias: PropTypes.string,
      iconPalette: PropTypes.arrayOf(PropTypes.string),
      color: PropTypes.string,
    })
  ),
};

export default FancyCards;
